/**
 * Bascule d'une trace vers son itinéraire routier, et retour.
 *
 * Le GPX d'origine n'est jamais modifié : revenir aux points d'origine le
 * relit, ce qui rend la bascule réversible sans le moindre appel réseau.
 *
 * Référence : `RouteController.kt`, `docs/gpx-import-export.md`.
 */

import type { GpxTrack } from '../core/track'
import type { Storage } from '../core/storage'
import type { Translator } from '../i18n'
import type { TrackPoint, TrackSegment, RoutingProfile } from '../core/types'
import { ROUTING_PROFILES, routingProfileFromName } from '../core/types'
import { apiProfileName } from '../core/osrm'
import { routeThrough, sampleWaypoints } from '../core/routing'
import { runElevationTaskAsync } from '../core/elevationWorker'
import { chooseDialog } from './dialogs'
import type { Shell } from './shell'

/** Clé du cache de l'itinéraire d'une trace. */
export function routeCacheKey(trackId: string): string {
  return `route_${trackId}`
}

export interface RouteDeps {
  readonly shell: Shell
  readonly translator: Translator
  readonly storage: Storage
  readonly tracks: () => readonly GpxTrack[]
  /** Redessine la trace et enregistre les modifications. */
  readonly onTrackChanged: (track: GpxTrack) => void
  /** Rafraîchit la ligne du tiroir, pour l'avancement. */
  readonly onProgress: () => void
  /**
   * Relit le GPX d'origine, pour le retour au tracé d'origine.
   *
   * Rend les **segments** et non des points à plat : la découpe en `<trkseg>`
   * du fichier doit ressortir intacte, comme le fait `RouteController.kt`.
   */
  readonly originalSegments: (track: GpxTrack) => Promise<TrackSegment[] | null>
  /**
   * Réapplique les altitudes récupérées après un retour au tracé d'origine.
   *
   * Le fichier relu n'en porte pas : sans cette reprise, revenir en arrière
   * effacerait un profil obtenu en ligne, qu'il faudrait redemander.
   */
  readonly restoreElevations: (track: GpxTrack) => Promise<void>
}

export class RouteController {
  #jobs = new Map<string, AbortController>()

  constructor(private readonly deps: RouteDeps) {}

  isRunning(track: GpxTrack): boolean {
    return this.#jobs.has(track.id)
  }

  /** Bascule : calcul si la trace est d'origine, retour si elle est routée. */
  toggle(track: GpxTrack): void {
    if (this.#jobs.has(track.id)) return

    // Une trace dont le fichier se déclarait routé n'a pas de tracé d'origine
    // à relire : la bascule ne lui propose que le recalcul. La garde est ici
    // autant que dans les réglages — c'est le seul endroit qui décide, et un
    // autre appelant ne doit pas pouvoir contourner l'invariant.
    if (track.isRouted && !track.fileDeclaresRouted) {
      void this.#revert(track)
      return
    }

    this.#chooseProfile((profile) => {
      track.routingProfile = profile
      void this.#calculate(track)
    })
  }

  /**
   * Route une trace posée à la main, sans demander de profil.
   *
   * Appelée après la proposition faite à l'import, où le profil courant de la
   * trace fait foi — poser une seconde question par trace pour un lot qu'on
   * vient d'accepter en bloc serait pénible, et Android ne le fait pas non
   * plus.
   *
   * Rend une promesse que l'import **attend** : la récupération d'altitude qui
   * suit doit porter sur la géométrie routée, sinon le profil se calcule sur
   * les cordes et sera à refaire.
   */
  async routeSparse(track: GpxTrack): Promise<void> {
    if (this.#jobs.has(track.id)) return
    await this.#calculate(track)
  }

  /**
   * Demande l'arrêt du calcul en cours.
   *
   * L'entrée n'est **pas** retirée ici : c'est la tâche elle-même qui la rend,
   * dans son `finally`, une fois qu'elle a constaté son abandon. La retirer
   * tout de suite rouvrait la porte à un second calcul pendant que le premier
   * finissait de se dérouler, et le `finally` tardif du premier éteignait
   * alors le voyant du second.
   */
  cancel(track: GpxTrack): void {
    this.#jobs.get(track.id)?.abort()
    void this.deps.storage.deleteCache(routeCacheKey(track.id))
  }

  #chooseProfile(onChosen: (profile: RoutingProfile) => void): void {
    const t = this.deps.translator

    chooseDialog(document.body, {
      title: t.t('routing_profile_label'),
      cancelLabel: t.t('cancel'),
      choices: Object.values(ROUTING_PROFILES).map((profile) => ({
        value: profile.id,
        label: t.t(
          profile.id === 'CAR'
            ? 'routing_profile_car'
            : profile.id === 'BIKE'
              ? 'routing_profile_bike'
              : 'routing_profile_foot',
        ),
      })),
      onChoose: (id) => onChosen(routingProfileFromName(id)),
    })
  }

  async #calculate(track: GpxTrack): Promise<void> {
    const t = this.deps.translator
    const controller = new AbortController()
    this.#jobs.set(track.id, controller)

    track.isRouting = true
    track.taskProgress = 0
    track.taskLabel = t.t('routing_in_progress')
    this.deps.onProgress()

    try {
      const originalPoints = track.allPoints

      // Les points d'intérêt du fichier priment quand il y en a : ce sont eux
      // que l'organisateur a placés, la trace n'étant qu'un tracé indicatif.
      const source =
        track.waypoints.length >= 2
          ? track.waypoints.map((waypoint) => ({
              lat: waypoint.lat,
              lon: waypoint.lon,
            }))
          : sampleWaypoints(originalPoints)

      const routed = await routeThrough(source, {
        profile: apiProfileName(track.routingProfile),
        signal: controller.signal,
        onProgress: ({ ratio }) => {
          track.taskProgress = ratio
          this.deps.onProgress()
        },
      })

      if (routed === null) {
        this.deps.shell.showSnackbar(t.t('routing_error'))
        return
      }

      if (!this.deps.tracks().includes(track)) return

      // Le report des altitudes est le calcul le plus lourd du portage : il
      // part sur un fil séparé, sans quoi la carte se figerait plusieurs
      // secondes sur une trace longue.
      const withElevation = await runElevationTaskAsync({
        kind: 'route-elevations',
        routePoints: routed,
        originalPoints,
      })

      // Le report d'altitudes ne s'interrompt pas — il tourne sur un fil qui
      // n'écoute pas le signal —, si bien qu'une annulation tombée pendant ce
      // calcul le laisse finir. Sans ce contrôle, la trace se retrouverait
      // routée après que l'utilisateur a demandé l'arrêt, et le tiroir
      // n'afficherait plus rien pour le lui dire.
      if (controller.signal.aborted) return

      const cached = await this.deps.storage.putCache(
        routeCacheKey(track.id),
        withElevation,
      )

      // Pour presque toutes les traces, un cache non écrit est sans
      // conséquence : la géométrie se reconstruit depuis le fichier source ou
      // par un nouveau calcul. **Sauf** pour une trace née des seuls points
      // d'intérêt — son fichier ne porte aucun `<trk>`, et son itinéraire ne
      // vit que dans ce cache. Sans lui, la restauration le trouve absent,
      // `isRouted` repasse à faux, et la trace revient vide au prochain
      // démarrage. Le dire tant que l'utilisateur peut encore agir vaut mieux
      // que de le lui laisser découvrir le lendemain.
      if (!cached && !track.includeFileTracks) {
        this.deps.shell.showSnackbar(t.t('route_cache_failed'))
      }

      track.setSegments([{ points: withElevation }])
      track.isRouted = true
      track.geometryModified = true
      this.deps.onTrackChanged(track)

      this.deps.shell.showSnackbar(t.t('routing_success'))
    } catch (error) {
      // Une annulation est attendue — trace supprimée, nouveau calcul — et ne
      // mérite rien. Toute autre erreur est un défaut, et un défaut avalé en
      // silence est indiscernable d'une application figée.
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('[VeloGPX] Calcul d’itinéraire interrompu', error)
        this.deps.shell.showSnackbar(t.t('routing_error'))
      }
    } finally {
      // Nettoyage **seulement si l'on est encore le job courant**. Une
      // annulation retire l'entrée aussitôt, l'utilisateur peut relancer, et
      // cette tâche-ci peut mettre encore plusieurs secondes à se rendre
      // compte de son abandon : son `finally` tardif effaçait alors l'entrée
      // du job NEUF. La garde anti-double-tap tombait, et l'annulation ne
      // trouvait plus rien à interrompre.
      //
      // Tout le nettoyage est sous la garde, pas seulement l'entrée : sinon la
      // tâche abandonnée éteindrait le voyant et l'avancement du job neuf, qui
      // tourne pourtant.
      if (this.#jobs.get(track.id) === controller) {
        this.#jobs.delete(track.id)
        track.isRouting = false
        track.taskProgress = null
        track.taskLabel = null
        this.deps.onProgress()
      }
    }
  }

  /** Retour au tracé d'origine, relu depuis le fichier conservé. */
  async #revert(track: GpxTrack): Promise<void> {
    const segments = await this.deps.originalSegments(track)
    if (segments === null) {
      this.deps.shell.showSnackbar(this.deps.translator.t('routing_error'))
      return
    }

    track.setSegments(segments)
    track.isRouted = false
    // Seul endroit qui le repose : la géométrie redevient celle du fichier,
    // donc l'export n'a plus rien à réécrire. `restoreElevations` juste
    // dessous peut le reposer à vrai, et c'est voulu — des altitudes
    // récupérées sont bien une géométrie qui s'écarte du fichier.
    track.geometryModified = false
    await this.deps.storage.deleteCache(routeCacheKey(track.id))

    await this.deps.restoreElevations(track)
    this.deps.onTrackChanged(track)
  }

  /**
   * Réapplique l'itinéraire enregistré au démarrage.
   *
   * Sans cela, une trace routée reviendrait à son tracé d'origine à chaque
   * redémarrage, alors que le réglage la dit routée.
   */
  async restore(track: GpxTrack): Promise<void> {
    if (!track.isRouted) return

    const cached = await this.deps.storage.getCache<TrackPoint[]>(
      routeCacheKey(track.id),
    )

    if (cached && cached.length >= 2) {
      track.setSegments([{ points: cached }])
      // La géométrie restaurée est celle du calcul, pas celle du fichier :
      // sans ce drapeau, le premier export après un redémarrage réécrirait le
      // tracé d'origine sous une trace que tout le reste annonce routée.
      track.geometryModified = true

      // **Et il faut le dire.** La couche de carte a été posée par `addTrack`
      // avec la géométrie du fichier ; sans cette annonce, rien ne la
      // redessine, et la carte garde le tracé d'origine sous une trace que le
      // tiroir annonce routée. Signalé à l'usage après un retour dans
      // l'application : il fallait revenir au tracé d'origine puis recalculer
      // pour que les deux se rejoignent.
      this.deps.onTrackChanged(track)
      return
    }

    // Une trace dont le **fichier** se déclare routé n'a jamais eu de cache à
    // perdre : ses `<trkseg>` sont déjà le résultat d'un calcul, relus du
    // fichier comme le reste. La démentir ici lui faisait perdre son état à
    // chaque redémarrage — et, à l'export suivant, l'annotation
    // `velogpx:routed` avec lui : le fichier rendu ne disait plus ce que le
    // fichier reçu disait.
    if (track.fileDeclaresRouted) return

    // Itinéraire perdu : la trace redevient ce qu'elle est réellement plutôt
    // que de se prétendre routée. La géométrie, elle, n'a pas bougé — c'est
    // celle du fichier —, mais l'indicateur du tiroir doit suivre, et
    // l'enregistrement avec lui : sans quoi la trace se reprétendrait routée
    // au démarrage suivant.
    track.isRouted = false
    this.deps.onTrackChanged(track)
  }
}
